import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { AppShell } from './app/AppShell.tsx'
import { Home } from './routes/Home.tsx'
import { MyCampaigns } from './routes/MyCampaigns.tsx'
import { CampaignOverview } from './routes/CampaignOverview.tsx'
import { Team } from './routes/Team.tsx'
import { Today } from './routes/Today.tsx'
import {
  Billing,
  CustomerPage,
  Customers,
  OneOff,
  PlatformCampaignPage,
  PlatformCampaigns,
  VoterData,
} from './routes/Platform.tsx'
import { UniverseList } from './routes/UniverseList.tsx'
import { UniverseBuilder } from './routes/UniverseBuilder.tsx'
import { SurveyList } from './routes/SurveyList.tsx'
import { SurveyBuilder } from './routes/SurveyBuilder.tsx'
import { Lists } from './routes/Lists.tsx'
import { Admin } from './routes/Admin.tsx'
import { Canvassers } from './routes/Canvassers.tsx'
import { Walks } from './routes/Walks.tsx'
import { Security } from './routes/Security.tsx'
import { Keying } from './routes/Keying.tsx'
import { Progress } from './routes/Progress.tsx'
import { Canvasses } from './routes/Canvasses.tsx'
import { CanvassNew, CanvassPage, type Step } from './routes/CanvassPage.tsx'
import { Scripts } from './routes/Scripts.tsx'


/*
 * Code-based routes, not file-based. File-based routing means a codegen step and
 * a generated file in the tree; with a handful of routes that is tooling we do
 * not need yet. Switch if the route count ever justifies it.
 */

const rootRoute = createRootRoute({ component: AppShell })

const STEPS: Step[] = ['basics', 'who', 'walkers', 'questions', 'publish']

const canvassRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/canvasses/$canvassId',
  validateSearch: (search: Record<string, unknown>): { step?: Step } =>
    STEPS.includes(search.step as Step) ? { step: search.step as Step } : {},
  component: function CanvassRoute() {
    const { canvassId } = canvassRoute.useParams()
    const { step } = canvassRoute.useSearch()
    return <CanvassPage canvassId={canvassId} step={step} />
  },
})

const routeTree = rootRoute.addChildren([
  // Where "/" lands depends on who you are (Home). Then: My campaigns for
  // anyone holding several, the campaign workspace, and /platform/* for
  // SuperAdmin and Org Admins (docs/decisions.md 2026-09-30).
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: Home }),
  createRoute({ getParentRoute: () => rootRoute, path: '/campaigns', component: MyCampaigns }),
  createRoute({ getParentRoute: () => rootRoute, path: '/overview', component: CampaignOverview }),
  createRoute({ getParentRoute: () => rootRoute, path: '/team', component: Team }),
  createRoute({ getParentRoute: () => rootRoute, path: '/universes', component: UniverseList }),
  createRoute({ getParentRoute: () => rootRoute, path: '/universes/new', component: UniverseBuilder }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/universes/$universeId',
    component: UniverseBuilder,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/surveys', component: SurveyList }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/surveys/$versionId',
    component: SurveyBuilder,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/lists', component: Lists }),
  /*
   * The map route is loaded on demand.
   *
   * MapLibre is ~800KB and took the shared bundle from 600KB to 1.4MB — paid on
   * every page load by everyone who never opens a map, which is most people
   * most of the time. It is the only route heavy enough to be worth splitting;
   * splitting the rest would be ceremony.
   */
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/turf',
    component: lazyRouteComponent(() => import('./routes/Turf.tsx'), 'Turf'),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/progress', component: Progress }),
  createRoute({ getParentRoute: () => rootRoute, path: '/keying', component: Keying }),
  createRoute({ getParentRoute: () => rootRoute, path: '/canvassers', component: Canvassers }),
  createRoute({ getParentRoute: () => rootRoute, path: '/walks', component: Walks }),
  // The Canvass (overhaul step 2, decisions.md 2026-10-25).
  createRoute({ getParentRoute: () => rootRoute, path: '/canvasses', component: Canvasses }),
  createRoute({ getParentRoute: () => rootRoute, path: '/canvasses/new', component: CanvassNew }),
  canvassRoute,
  createRoute({ getParentRoute: () => rootRoute, path: '/scripts', component: Scripts }),
  createRoute({ getParentRoute: () => rootRoute, path: '/security', component: Security }),

  createRoute({ getParentRoute: () => rootRoute, path: '/platform/today', component: Today }),
  createRoute({ getParentRoute: () => rootRoute, path: '/platform/customers', component: Customers }),
  createRoute({ getParentRoute: () => rootRoute, path: '/platform/billing', component: Billing }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/platform/customers/$orgId',
    component: CustomerPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/platform/one-off', component: OneOff }),
  createRoute({ getParentRoute: () => rootRoute, path: '/platform/campaigns', component: PlatformCampaigns }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/platform/campaigns/$campaignId',
    component: PlatformCampaignPage,
    // Which tab: Today's "Needs you" links straight to Licenses or Paperwork.
    validateSearch: (search: Record<string, unknown>): { tab?: 'team' | 'licenses' | 'paperwork' } => {
      const t = search.tab
      return t === 'team' || t === 'licenses' || t === 'paperwork' ? { tab: t } : {}
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/platform/people', component: Admin }),
  createRoute({ getParentRoute: () => rootRoute, path: '/platform/voter-data', component: VoterData }),

  // Old addresses, kept working for bookmarks.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin',
    beforeLoad: () => {
      throw redirect({ to: '/platform/people' })
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/snapshots',
    beforeLoad: () => {
      throw redirect({ to: '/platform/voter-data' })
    },
  }),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
